import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { MapPin, TrendingUp, DollarSign, ExternalLink, Globe, Users, Award, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UniversityDetailPage() {
  const { id } = useParams();
  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUniversity = async () => {
      try {
        const response = await axios.get(`${API}/universities/${id}`);
        setUniversity(response.data);
      } catch (error) {
        console.error('Error fetching university:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUniversity();
  }, [id]);

  if (loading) {
    return (
      <div className="py-12">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="h-96 bg-secondary animate-pulse rounded-sm" />
        </div>
      </div>
    );
  }

  if (!university) {
    return (
      <div className="py-12">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h1 className="text-3xl font-playfair font-bold mb-4">University Not Found</h1>
          <Link to="/universities">
            <Button>Back to Universities</Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatTuition = (min, max) => {
    return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k per year`;
  };

  return (
    <div className="py-12 bg-white">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="relative overflow-hidden rounded-sm mb-8">
          <img
            src={university.banner_image}
            alt={university.name}
            className="w-full h-64 md:h-96 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
            <div className="p-6 md:p-8 w-full">
              <div className="flex items-center gap-4 mb-3">
                <img src={university.logo} alt={`${university.name} logo`} className="w-16 h-16 rounded-sm bg-white p-2" />
                <div>
                  <h1 className="text-3xl md:text-4xl font-playfair font-bold text-white">{university.name}</h1>
                  <p className="text-white/90 flex items-center gap-2 mt-1">
                    <MapPin size={16} />
                    {university.region}, {university.country}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="mb-8">
              <h2 className="text-2xl font-playfair font-bold text-foreground mb-4">About {university.name}</h2>
              <p className="text-muted-foreground leading-relaxed">{university.description}</p>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-playfair font-bold text-foreground mb-4">Programs Offered</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {university.programs.map((program, idx) => (
                  <div key={idx} className="border border-border/40 rounded-sm p-4 flex items-center gap-3">
                    <BookOpen size={20} className="text-accent" />
                    <span className="font-medium">{program}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-playfair font-bold text-foreground mb-4">Highlights</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {university.highlights.map((highlight, idx) => (
                  <div key={idx} className="bg-secondary rounded-sm p-4 flex items-center gap-3">
                    <Award size={20} className="text-accent" />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <div className="border border-border/40 rounded-sm p-6 sticky top-24">
              <h3 className="text-xl font-playfair font-bold text-foreground mb-4">Quick Facts</h3>
              
              <div className="space-y-4 mb-6">
                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <TrendingUp size={16} />
                    <span>Global Ranking</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">#{university.ranking_global}</div>
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Users size={16} />
                    <span>Acceptance Rate</span>
                  </div>
                  <div className="text-2xl font-bold text-foreground">{university.acceptance_rate}%</div>
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <DollarSign size={16} />
                    <span>Tuition (Annual)</span>
                  </div>
                  <div className="text-lg font-bold text-foreground">{formatTuition(university.tuition_min, university.tuition_max)}</div>
                </div>

                <div>
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Globe size={16} />
                    <span>Website</span>
                  </div>
                  <a href={university.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline text-sm break-all">
                    {university.website}
                  </a>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  className="w-full rounded-sm"
                  onClick={() => window.open(university.website, '_blank')}
                  data-testid="visit-website-btn"
                >
                  <ExternalLink size={16} className="mr-2" />
                  Visit Website
                </Button>
                <Button variant="outline" className="w-full rounded-sm" data-testid="apply-now-btn">
                  Apply Now
                </Button>
                <Button variant="outline" className="w-full rounded-sm" data-testid="download-brochure-btn">
                  Download Brochure
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}